import {
  Autocomplete,
  Box,
  Checkbox,
  createFilterOptions,
  Grid,
  List,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { endOfDay, format, isValid, parseISO, startOfDay } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import {
  SelectLight,
  TextFieldLight,
} from "../../../../common/components/styled/styled";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";
import useMessage from "../../../../hooks/useMessage";
import {
  ManualSalary,
  ManualSalaryRepeatType,
  ManualSalaryUnitType,
} from "../../../talents/talentAPI";
import {
  addManualSalary,
  editManualSalary,
} from "../../../talents/talentsSlice";
import {
  CreateManualSalary,
  EditManualSalary,
} from "../../../talents/types/commands";
import {
  AssignmentOption,
  CcOption,
  ExpenseTypeOption,
  Option,
} from "./ManualSalaries";
import {
  ExpenseTypeGroupNames,
  hasManualSalaryAssignment,
  MANUAL_SALARY_ASSIGNMENT_NOT_SELECTED,
  manualSalaryAssignmentDefaults,
  manualSalaryTargetPayload,
} from "./helpers";
import { DateRange } from "@mui/x-date-pickers-pro";
import InputHelperText from "../../../../common/components/functional/error/InputHelperText";
import isEmpty from "../../../../common/helpers/isEmpty";
import { AutocompleteBaseSingle } from "../../../../common/components/functional/fields/autocompleteBase/AutocompleteBaseSingle";
import AutocompleteGroupedList from "../../../../common/components/functional/AutocompleteGroupedList/AutocompleteGroupedList";

interface Props {
  close: () => void;
  manualSalary: ManualSalary | null;
  assignmentOptions: AssignmentOption[];
  ccOptions: CcOption[];
  expenseTypeOptions: ExpenseTypeOption[];
  eezyCcOptions: Option[];
  typeOptions: Option[];
  employmentId: string;
  employmentStart: string;
  employmentEnd: string;
  employerName: string;
  updateManualSalaries: (manualSalary: ManualSalary) => void;
  isLoading: boolean;
}

export const ManualSalaryForm = ({
  close,
  manualSalary,
  assignmentOptions,
  ccOptions,
  expenseTypeOptions,
  eezyCcOptions,
  typeOptions,
  employmentId,
  employmentStart,
  employmentEnd,
  employerName,
  updateManualSalaries,
  isLoading,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { sendMessage } = useMessage();

  const expenseTypeTranslations = useAppSelector(
    (state) => state.localized.expenseType,
  );
  const unitTypeTranslations = useAppSelector((state) => state.localized.unit);

  const groupName =
    expenseTypeOptions.find(
      (option) => option.value === manualSalary?.expenseTypeCode,
    )?.group || "";
  // TODO stop using notSelected instead use null for not selected assignment.
  // For now this tries to solve issue where assignmentId/kohdistus needs to be user-selected and
  // should not have any selection by default.
  const notSelected = MANUAL_SALARY_ASSIGNMENT_NOT_SELECTED;

  const getParsedDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = parseISO(dateString);
    return isValid(date) ? new Date(date) : null;
  };

  const getManualSalaryDateDefaultValue = useCallback(() => {
    return getParsedDate(manualSalary?.date) || getParsedDate(employmentStart);
  }, [employmentStart, manualSalary?.date]);

  const defaultValues = useMemo(
    () => ({
      type: groupName,
      expenseTypeCode: manualSalary?.expenseTypeCode || "",
      description: manualSalary?.description || "",
      date: getManualSalaryDateDefaultValue(),
      amount: manualSalary?.amount || "",
      unitCost: manualSalary?.unitCost || "",
      unit: manualSalary?.unit || ManualSalaryUnitType.NONE,
      repeatType: manualSalary?.repeatType || ManualSalaryRepeatType.NONE,
      repeatDuration: [
        // for UX reasons, this is a date range field where the first date is the manual salary date
        manualSalary?.date ? new Date(manualSalary?.date) : null,
        manualSalary?.repeatUntil ? new Date(manualSalary?.repeatUntil) : null,
      ] as DateRange<Date>,
      ...manualSalaryAssignmentDefaults(manualSalary),
      isBillable: manualSalary?.isBillable || false,
    }),
    [manualSalary, groupName, getManualSalaryDateDefaultValue],
  );

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    reset,
    setValue,
    control,
    watch,
    trigger,
    formState: { isDirty, isValid: isValidForm, errors, dirtyFields },
  } = methods;

  const type = watch("type");
  const expenseType = watch("expenseTypeCode");
  const assignmentId = watch("assignmentId");
  const repeatsMonthly = watch("repeatType") === ManualSalaryRepeatType.MONTHLY;
  const manualSalaryDate = watch("date");

  const needsNegativeUnitCost =
    expenseType === "asuntovuokra_ei_laskutukseen" ||
    expenseType === "ateriavahennys_ei_laskutukseen";
  const expenseTypeUnbillable =
    type === ExpenseTypeGroupNames.Luontoisetu ||
    (type === ExpenseTypeGroupNames.Nettomuutos &&
      expenseType !== "asuntovuokra_(edelleenveloitus)");
  const noAssignmentSelected = !assignmentId || assignmentId === notSelected;

  // Disable checkbox for luontoisetu and nettomuutos categories (except asuntovuokra (edelleenveloitus))
  // or if no assignment is selected
  const disableBillable = useMemo(
    () => expenseTypeUnbillable || noAssignmentSelected,
    [expenseTypeUnbillable, noAssignmentSelected],
  );

  const filteredExpenseTypes = expenseTypeOptions.filter(
    (typeOption) => typeOption.group === type,
  );
  const filteredCcOptions = ccOptions.filter(
    (option) => option.assignmentId === assignmentId,
  );

  const onSave = () => {
    const unit = methods.getValues().unit;
    const manualSalaryValues = {
      ...methods.getValues(),
      date: format(methods.getValues().date as Date, isoFormat),
      description: methods.getValues().description || null,
      amount: methods.getValues().amount,
      unitCost: methods.getValues().unitCost,
      unit: unit === ManualSalaryUnitType.NONE ? null : unit,
      ...(methods.getValues().repeatDuration &&
        methods.getValues().repeatDuration[1] && {
          repeatUntil: format(
            methods.getValues().repeatDuration[1] as Date,
            isoFormat,
          ),
        }),
      repeatType: repeatsMonthly
        ? ManualSalaryRepeatType.MONTHLY
        : ManualSalaryRepeatType.NONE,
      // A real assignment requires a customer cost center and no Eezy cost center.
      // The notSelected sentinel is not an assignment: customer cost center must be null.
      ...manualSalaryTargetPayload(methods.getValues()),
    };

    if (manualSalary?.manualSalaryId) {
      const { repeatDuration, ...details } = manualSalaryValues;
      // Edit existing
      const payload: EditManualSalary = {
        manualSalaryId: manualSalary.manualSalaryId,
        version: manualSalary.version,
        details: details,
      };
      dispatch(editManualSalary(payload)).then((action) => {
        if (isFulfilled(action)) {
          const { repeatDuration, ...rest } = manualSalaryValues;

          const manualSalary: ManualSalary = {
            ...rest,
            ...(repeatDuration &&
              repeatDuration[1] && {
                repeatUntil: format(repeatDuration[1], isoFormat),
              }),
            version: action.payload.version,
            manualSalaryId: action.payload.id,
          };
          sendMessage(t("savedSuccessfully"), "success");
          updateManualSalaries(manualSalary);
          close();
        }
      });
    } else {
      const { repeatDuration, ...details } = manualSalaryValues;

      // Create new
      const payload: CreateManualSalary = {
        employmentId,
        details: details,
      };
      dispatch(addManualSalary(payload)).then((action) => {
        if (isFulfilled(action)) {
          const { repeatDuration, ...rest } = manualSalaryValues;

          const manualSalary: ManualSalary = {
            ...rest,
            ...(repeatDuration &&
              repeatDuration[1] && {
                repeatUntil: format(repeatDuration[1], isoFormat),
              }),
            version: action.payload.version,
            manualSalaryId: action.payload.id,
          };
          sendMessage(t("savedSuccessfully"), "success");
          updateManualSalaries(manualSalary);
          close();
        }
      });
    }
  };

  useEffect(() => {
    reset(defaultValues);
  }, [manualSalary, reset, defaultValues]);

  // Set unit based on selected expense type
  useEffect(() => {
    if (!dirtyFields.expenseTypeCode) return;
    const unit = expenseTypeOptions.find(
      (option) => option.value === expenseType,
    )?.unit;
    setValue("unit", unit || ManualSalaryUnitType.NONE);
  }, [expenseType, setValue, expenseTypeOptions, dirtyFields.expenseTypeCode]);

  // Set billing  in form to false with certain expense types and when no assignment is selected
  useEffect(() => {
    if (expenseTypeUnbillable || noAssignmentSelected) {
      setValue("isBillable", false);
    }
  }, [expenseTypeUnbillable, noAssignmentSelected, setValue]);

  // Clear some values when type or assignment changes

  useEffect(() => {
    if (!dirtyFields.type) return;
    setValue("expenseTypeCode", "");
  }, [type, setValue, dirtyFields.type]);

  useEffect(() => {
    if (!dirtyFields.assignmentId) return;
    setValue("customerCostCenterId", "", { shouldValidate: true });
    setValue("eezyCostCenterId", "", { shouldValidate: true });
  }, [assignmentId, setValue, dirtyFields.assignmentId]);

  useEffect(() => {
    // if the manual salary date or repeatsMonthly changes, reset the repeatDuration start date
    if (dirtyFields?.repeatType || dirtyFields?.date) {
      setValue("repeatDuration", [manualSalaryDate, null]);
      // trigger validation for repeatDuration
      trigger("repeatDuration");
    }
  }, [manualSalaryDate, repeatsMonthly, setValue, dirtyFields, trigger]);

  useEffect(() => {
    isDirty && setValue("unitCost", "");
    // Empty unit cost field when negative unit cost boolean is evaluated again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsNegativeUnitCost, setValue]);

  const validateManualSalaryDate = (date: Date | null) => {
    const start = new Date(employmentStart).setHours(0, 0, 0, 0);
    const end = employmentEnd
      ? new Date(employmentEnd).setHours(0, 0, 0, 0)
      : null;

    const dateValue = date ? date.setHours(0, 0, 0, 0) : null;
    if (!dateValue) return false;

    // if employmentEnd is given, date must be within employment
    if (end) {
      if (dateValue < start || dateValue > end) {
        return t("talentProfile:dateOutsideEmployment");
      }
    } else {
      // if employmentEnd is not given, date must be after employmentStart
      if (dateValue < start) {
        return t("talentProfile:dateBeforeEmploymentStart");
      }
    }

    return true;
  };

  const resetRepeatDuration = () =>
    setValue("repeatDuration", [methods.getValues("date"), null]);

  const validateRepeatDuration = (
    value: [Date | null, Date | null],
  ): boolean | string => {
    // if not repeating monthly, do not perform any checks
    if (!repeatsMonthly) {
      return true;
    }

    const [startRange, endRange] = value;

    // employment start and end dates (if end date is set)
    const employmentStartDate = startOfDay(new Date(employmentStart));
    const employmentEndDate = employmentEnd
      ? endOfDay(new Date(employmentEnd))
      : null;

    // the start date is always required
    if (!startRange || !isValid(startRange)) {
      return t("common:invalidDate");
    }
    const startRangeDate = startOfDay(startRange);

    // the start date cannot be earlier than the employment start date
    if (startRangeDate < employmentStartDate) {
      return t("talentProfile:repeatingStartDateBeforeEmploymentStart");
    }
    // if the employment end date is defined, ensure the start date is not after it
    if (employmentEndDate && startRangeDate > employmentEndDate) {
      return t("talentProfile:repeatingRangeOutsideEmployment");
    }

    // end range is required if repeating monthly and employment end date is defined
    if (employmentEndDate && !endRange) {
      return t("talentProfile:repeatingRangeEndRequired");
    }

    // the end date is optional
    if (endRange) {
      // if the end date is provided, it must be valid
      if (!isValid(endRange)) {
        return t("common:invalidDate");
      }
      const endRangeDate = endOfDay(endRange);

      // the start date cannot be later than the end date
      if (startRangeDate > endRangeDate) {
        return t("talentProfile:repeatingStartDateAfterEndDate");
      }

      // if the employment end date is defined, ensure the end date does not exceed it
      if (employmentEndDate && endRangeDate > employmentEndDate) {
        return t("talentProfile:repeatingRangeOutsideEmployment");
      }

      // the end date cannot be the 29th, 30th, or 31st day
      const endDayOfMonth = endRangeDate.getDate();
      if ([29, 30, 31].includes(endDayOfMonth)) {
        return t("talentProfile:repeatingRangeEndInvalidDate");
      }
    }

    return true;
  };

  return (
    <DialogContentWrapper
      title={
        manualSalary?.manualSalaryId
          ? t("talentProfile:editManualSalary")
          : t("talentProfile:addManualSalary")
      }
      onCancel={close}
      onSave={onSave}
      saveDisabled={
        !isDirty ||
        !isEmpty(errors) ||
        !isValidForm ||
        isLoading ||
        assignmentId === notSelected
      }
    >
      <FormProvider {...methods}>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">{t("talentProfile:type")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="type"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                value={{ value: value || "", label: value }}
                options={typeOptions}
                renderInput={(params) => <TextField {...params} size="small" />}
                onChange={(_, newValue: Option | null) =>
                  onChange(newValue?.value || "")
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:expenseType")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="expenseTypeCode"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                value={{
                  value: value || "",
                  label: "",
                  group: "",
                  unit: "",
                }}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                options={filteredExpenseTypes}
                getOptionLabel={(option) => {
                  return (
                    (option.value &&
                      expenseTypeTranslations &&
                      expenseTypeTranslations[option.value]) ||
                    option.value ||
                    ""
                  );
                }}
                filterOptions={createFilterOptions({
                  stringify: (option) => JSON.stringify(option),
                })}
                renderInput={(params) => <TextField {...params} size="small" />}
                renderGroup={({ group, children }) => (
                  <>
                    <ListSubheader
                      key={group}
                      disableSticky
                      sx={{
                        color: "#9167CA",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: "20px",
                      }}
                    >
                      <Typography variant="header">{group}</Typography>
                    </ListSubheader>
                    <List sx={{ paddingLeft: "32px" }}>{children}</List>
                  </>
                )}
                onChange={(_, newValue: Option | null) =>
                  onChange(newValue?.value || "")
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">{t("common:description")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="description"
            control={control}
            rules={{ maxLength: 128 }}
            render={({ field }) => (
              <TextFieldLight
                fullWidth
                size="small"
                {...field}
                value={field.value}
                inputProps={{ maxLength: 128 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">{t("common:date")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="date"
            control={control}
            rules={{
              required: true,
              validate: (value) => validateManualSalaryDate(value),
            }}
            render={({ field }) => (
              <DesktopDatePicker
                {...field}
                format={leadingZerosDateFormat}
                value={field.value}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!errors.date,
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:amount")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: true,
              validate: (value) => value.slice(-1) !== ".",
            }}
            render={({ field }) => (
              <NumericFormat
                value={field.value}
                decimalSeparator=","
                valueIsNumericString
                customInput={TextField}
                decimalScale={8}
                size="small"
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:unitPrice")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="unitCost"
            control={control}
            rules={{
              required: true,
              validate: (value) =>
                value.slice(-1) !== "." &&
                (needsNegativeUnitCost
                  ? parseFloat(value) <= 0
                  : parseFloat(value) >= 0),
            }}
            render={({ field }) => (
              <NumericFormat
                value={field.value}
                decimalSeparator=","
                valueIsNumericString
                customInput={TextField}
                decimalScale={8}
                size="small"
                allowNegative={needsNegativeUnitCost ? true : false}
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
                error={!!errors.unitCost}
                helperText={
                  errors.unitCost
                    ? needsNegativeUnitCost
                      ? t("talentProfile:unitCostPositiveError")
                      : t("talentProfile:unitCostNegativeError")
                    : ""
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">{t("invoices:unit")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="unit"
            control={control}
            render={({ field }) => (
              <TextFieldLight
                size="small"
                {...field}
                value={unitTypeTranslations[field.value] || "-"}
                disabled
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:repeatsMonthly")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="repeatType"
            control={control}
            render={({ field }) => (
              <SelectLight
                {...field}
                fullWidth
                size="small"
                sx={{ maxWidth: "180.76px" }}
                value={field.value}
                onChange={(e) => {
                  resetRepeatDuration();
                  field.onChange(e.target.value);
                }}
              >
                <MenuItem value={ManualSalaryRepeatType.NONE}>
                  {t("common:no")}
                </MenuItem>
                <MenuItem value={ManualSalaryRepeatType.MONTHLY}>
                  {t("common:yes")}
                </MenuItem>
              </SelectLight>
            )}
          />
        </Grid>
        {repeatsMonthly && (
          <>
            <Grid item xs={4} textAlign="end" mt="8px">
              <Typography variant="secondary">
                {t("talentProfile:repeatDuration")}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="repeatDuration"
                control={control}
                rules={{
                  validate: (value) => validateRepeatDuration(value),
                }}
                render={({ field: { value, onChange } }) => (
                  <Box
                    display="flex"
                    gap="4px"
                    alignItems="center"
                    width="100%"
                  >
                    <DesktopDatePicker
                      disabled
                      format={leadingZerosDateFormat}
                      value={value?.[0] ?? null}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          label: null,
                          error: !!errors?.repeatDuration,
                        },
                      }}
                    />
                    <Typography>-</Typography>
                    <DesktopDatePicker
                      format={leadingZerosDateFormat}
                      value={value?.[1] ?? null}
                      shouldDisableDate={(day) => {
                        const date = day.getDate();
                        // disable 29th, 30th and 31st from the picker
                        return date === 29 || date === 30 || date === 31;
                      }}
                      onChange={(newEndDate) => {
                        onChange([value?.[0] ?? null, newEndDate]);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          label: null,
                          error: !!errors?.repeatDuration,
                        },
                      }}
                    />
                  </Box>
                )}
              />
              <InputHelperText
                errorMessage={errors?.repeatDuration?.message}
                helperText={t("talentProfile:repeatingRangeOutsideEmployment")}
              />
            </Grid>
          </>
        )}
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:target")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="assignmentId"
            control={control}
            render={({ field }) => (
              <AutocompleteBaseSingle
                disableClearable
                value={
                  field.value
                    ? assignmentOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    : null
                }
                placeholder={t("talentProfile:selectTarget")}
                options={assignmentOptions}
                getOptionLabel={(option) => option.label}
                groupBy={(option) => {
                  const isShort = !!option.isShift || !!option.isTargetedShift;
                  return isShort.toString();
                }}
                renderGroup={({ group, children }) => {
                  const label =
                    group === "true"
                      ? t("talentProfile:shortOptions")
                      : t("talentProfile:longOptions");
                  return (
                    <AutocompleteGroupedList
                      group={label}
                      children={children}
                    />
                  );
                }}
                onChange={(newValue: Option | null) =>
                  field.onChange(newValue?.value || "")
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:costCenter")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          {!!assignmentId ? (
            <Controller
              name="customerCostCenterId"
              control={control}
              rules={{ required: hasManualSalaryAssignment(assignmentId) }}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  size="small"
                  fullWidth
                  value={field.value}
                  disabled={assignmentId === notSelected}
                >
                  {filteredCcOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          ) : (
            <Controller
              name="eezyCostCenterId"
              control={control}
              rules={{ required: !hasManualSalaryAssignment(assignmentId) }}
              render={({ field }) => (
                <SelectLight
                  {...field}
                  size="small"
                  fullWidth
                  value={field.value}
                  disabled={assignmentId === notSelected}
                >
                  {eezyCcOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          )}
        </Grid>

        <Grid item xs={4} textAlign="end" alignSelf="center">
          <Typography variant="secondary">
            {t("talentProfile:billable")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="isBillable"
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                disabled={disableBillable}
              />
            )}
          />
        </Grid>
      </FormProvider>
    </DialogContentWrapper>
  );
};
